/**
 * Canonical JSON for the forensic hash chain (audit-trail-design.md §2.2; CC-11).
 *
 * `payload_json` MUST be canonicalised before hashing — different serialisers
 * (V8, Python, Java) emit different key orders and the same logical payload would
 * hash differently, silently forking the chain. We use JCS (RFC 8785): keys sorted
 * lexicographically by UTF-16 code unit, ES6 number canonicalisation.
 *
 * Library mandate (F-082): ARGUS imports the `canonicalize` npm package (the
 * CyberPhone/json-canonicalization reference impl) rather than re-implementing
 * JCS, so we inherit upstream fixes. The hand-rolled reference in §2.2 is kept in
 * the spec for documentation only — production code (this module) uses the package.
 * Conformance vectors live in canonical-json.spec.ts and run on every CI build.
 *
 * No top-level side effects (CC-10).
 */
import canonicalize from 'canonicalize';

/**
 * JCS/RFC-8785 canonical serialisation of a JSON value. Throws on a
 * non-canonicalisable input (e.g. bare `undefined`) rather than returning a
 * non-string — the caller (computeRowHash / auditEvent) always passes a JSON
 * object, so this only fires on a programming error and must fail closed.
 */
export function canonicalJson(value: unknown): string {
  const out = canonicalize(value);
  if (out === undefined) {
    throw new Error('canonicalJson: value is not canonicalisable (undefined)');
  }
  return out;
}
